import { type FC, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  HeaderDivider,
  HStack,
  Item,
  MenuOptionItem,
  MenuTriggerButton,
  Popover,
  Section,
  TriggerButton,
  TriggerButtonIntent,
} from "widgets";
import { useOverlay } from "widgets/hooks";

import { type ProjectFile } from "@/graphql.ts";
import {
  type BrowsingDirectoryFragment,
  type BrowsingProjectFileFragment,
} from "@/modules/files/graphql/files-queries.gql.ts";
import { getParentPathFromProjectFile, isAnalysisSetFile, isGenomeViewerFile } from "@/modules/files/utils/file.ts";
import { notify } from "@/modules/notifications/notify.tsx";
import {
  downloadJnlpFile,
  getProjectFilesDataForSendingToGenomeViewer,
  pasteFiles,
  storeFilesToBeCopied,
  storeFilesToBeCut,
} from "@/redux/modules/files.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { EXT_APP_MODULES, FILE_BROWSE_TYPES } from "@/settings/settings.ts";
import { AddLinkDialog } from "@/ui-modules/files/AddLinkDialog.tsx";

import { CreateFolderHeaderAction } from "../../modules/files/components/CreateFolderHeaderAction.tsx";
import { FilesDeletionControlledAlertDialog } from "../../modules/files/components/FilesDeletionControlledAlertDialog.tsx";
import BamFileValidationDialog from "./BamFileValidationDialog.tsx";
import UploadStateButton from "./UploadStateButton.tsx";
import { checkAndDownloadProjectFiles, checkProjectFilesAvailability } from "@selector/shared/jsx";
import { selMessage } from "@selector/shared";

interface IFileBrowserActionWidgetsProps {
  selectedFiles: BrowsingProjectFileFragment[];
  resetSelection: () => void;
  handleClickUploadButton: () => void; // callback to be linked to the drop zone
  refreshCurrentFolder: () => Promise<void>;
  currentFolder: BrowsingDirectoryFragment;
}

export const FileBrowserActionWidgets: FC<IFileBrowserActionWidgetsProps & { renderOffScreen?: boolean }> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const triggerRef = useRef<HTMLButtonElement>(null);
  const { overlayProps, state, triggerProps } = useOverlay({});

  const [projectFilesForGenomeViewer, setProjectFilesForGenomeViewer] = useState<ProjectFile[]>();

  const [filesToBeCopied, filesToBeCut, fileOperationInProgress] = useSelector<
    IAppState,
    [BrowsingProjectFileFragment[] | null, BrowsingProjectFileFragment[] | null, boolean]
  >((state) => [state.files.filesToCopy, state.files.filesToCut, state.files.fileOperationInProgress], shallowEqual);

  const filesToBePasted = filesToBeCopied || filesToBeCut;

  const disabledDownloadTooltip = !props.selectedFiles.length
    ? "At least one file must be selected to perform a download."
    : props.selectedFiles.some(
          (file) =>
            file.fileBrowseType === FILE_BROWSE_TYPES.HYPERLINK || file.fileBrowseType === FILE_BROWSE_TYPES.DIRECTORY,
        )
      ? "Links and folder cannot be downloaded."
      : "";
  const disabledDeletion: boolean =
    !props.selectedFiles.length || props.selectedFiles.filter((file) => !file.deletable).length > 0;
  const disabledPasteOperation: boolean =
    !props.currentFolder?.isEditable ||
    !filesToBePasted?.length ||
    fileOperationInProgress ||
    // prevent the files to be cut in the same location
    !!filesToBeCut?.find((file) => getParentPathFromProjectFile(file) === props.currentFolder.fullPath);
  const disabledCopyOperation = !props.selectedFiles.length;
  const disabledCutOperation: boolean =
    !props.selectedFiles.length || !props.currentFolder?.isEditable || disabledDeletion;

  const disabledGenomeViewerMenuItem: boolean =
    !props.selectedFiles.length || props.selectedFiles.filter((file) => !isGenomeViewerFile(file)).length > 0;

  const disabledAnylastMenuItem: boolean =
    !props.selectedFiles.length || props.selectedFiles.filter((file) => !isAnalysisSetFile(file)).length > 0;

  const disabledKeys = useMemo(() => {
    const temporarilyDisabled = [];

    if (disabledGenomeViewerMenuItem) {
      temporarilyDisabled.push("send-to-genome-browser");
    }

    if (disabledAnylastMenuItem) {
      temporarilyDisabled.push("send-to-analyst");
    }

    return temporarilyDisabled;
  }, [disabledGenomeViewerMenuItem, disabledAnylastMenuItem]);

  function notifyUnavailableFiles(unavailableFiles: { fileName: string | null }[]) {
    const allNames = unavailableFiles.flatMap((f) => (f.fileName != null ? [f.fileName] : []));
    const listed = allNames.slice(0, 5);
    if (allNames.length > 5) {
      listed.push("...");
    }
    notify.warning(
      {
        title: selMessage({
          id: "file.download.failed.title",
        }),
        children: selMessage({
          id: "file.download.failed.children",
          values: { fileNames: listed.join("', '"), filesCount: unavailableFiles.length },
        }),
      },
      { autoClose: 5000 },
    );
  }

  function handleClickCopyButton() {
    if (!disabledCopyOperation && props.selectedFiles.length) {
      // Hyperlinks do not have a physical file on the server, skip availability check for them
      const filesToCheck = props.selectedFiles.filter((file) => file.fileBrowseType !== FILE_BROWSE_TYPES.HYPERLINK);
      if (filesToCheck.length === 0) {
        // We only have hyperlinks
        dispatch(storeFilesToBeCopied(props.selectedFiles));
        return;
      }
      void checkProjectFilesAvailability(filesToCheck).then(({ unavailable }) => {
        if (unavailable.length) {
          // If any of the selected files is not available, then we abort the operation
          notifyUnavailableFiles(unavailable);
        } else {
          dispatch(storeFilesToBeCopied(props.selectedFiles));
        }
      });
    }
  }

  function handleClickCutButton() {
    if (!disabledCutOperation && props.selectedFiles.length) {
      // Hyperlinks do not have a physical file on the server, skip availability check for them
      const filesToCheck = props.selectedFiles.filter((file) => file.fileBrowseType !== FILE_BROWSE_TYPES.HYPERLINK);
      if (!filesToCheck.length) {
        // We only have hyperlinks
        dispatch(storeFilesToBeCut(props.selectedFiles));
        return;
      }
      void checkProjectFilesAvailability(filesToCheck).then(({ unavailable }) => {
        if (unavailable.length) {
          // If any of the selected files is not available, then we abort the operation
          notifyUnavailableFiles(unavailable);
        } else {
          dispatch(storeFilesToBeCut(props.selectedFiles));
        }
      });
    }
  }

  function handleClickPasteButton() {
    if (!disabledPasteOperation) {
      dispatch(pasteFiles(props.currentFolder as Partial<ProjectFile>))
        .then((params) => {
          // refresh the current folder if it corresponds to the source or target folders
          if (params?.targetFolderId === props.currentFolder.id || params?.sourceFolderId === props.currentFolder.id) {
            void props.refreshCurrentFolder();
          }
        })
        .then(() => {
          props.resetSelection?.();
        });
    }
  }

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  return (
    <HStack spacing={1}>
      <CreateFolderHeaderAction
        currentFolder={props.currentFolder}
        onSaved={() => void props.refreshCurrentFolder()}
        isDisabled={!props.currentFolder?.isEditable}
        showLabel={false}
      />

      <UploadStateButton
        handleClickUploadButton={props.handleClickUploadButton}
        isDisabled={!props.currentFolder?.isEditable ? "This folder is not editable" : undefined}
        tooltip={"Upload files in the current location."}
        key={"file-browser-widget-upload-state"}
        showLabel={false}
      />

      <TriggerButton
        {...triggerProps}
        icon={"add_link"}
        tooltip={"Create a new link in the current location"}
        isDisabled={!props.currentFolder?.isEditable ? "This folder is not editable" : undefined}
        label={"New Link"}
        showLabel={false}
        ref={triggerRef}
      />

      <Popover state={state} triggerRef={triggerRef} {...overlayProps}>
        <AddLinkDialog
          onSaved={() => void props.refreshCurrentFolder()}
          currentFolder={props.currentFolder}
          onClose={state.close}
        />
      </Popover>

      <HeaderDivider key={"file-browser-widget-divider"} />
      <TriggerButton
        key={"file-browser-widget-download"}
        isDisabled={disabledDownloadTooltip}
        onPress={() =>
          void checkAndDownloadProjectFiles(props.selectedFiles).then((unavailableFiles) => {
            if (unavailableFiles.length) {
              notifyUnavailableFiles(unavailableFiles);
            }
          })
        }
        icon={"download"}
        label={"Download"}
        showLabel={false}
      />

      <MenuTriggerButton
        key={"file-browser-widget-analyze"}
        icon={"query_stats"}
        label={"Analyze"}
        tooltip={"Analyze selected files"}
        showLabel={false}
        disabledKeys={disabledKeys}
        onAction={(key) => {
          switch (key) {
            case "send-to-genome-browser":
              setProjectFilesForGenomeViewer(
                dispatch(getProjectFilesDataForSendingToGenomeViewer(props.selectedFiles.map((file) => file.id))),
              );
              break;
            case "send-to-analyst":
              void dispatch(
                downloadJnlpFile({
                  moduleId: EXT_APP_MODULES.ANALYST.id,
                  fileIds: props.selectedFiles.map((file) => file.id),
                }),
              );
              break;
          }
        }}
      >
        <Section title={"Show selected files in"}>
          <Item key={"send-to-genome-browser"}>
            <MenuOptionItem
              title={"Genome Viewer"}
              id={"send-to-genome-browser"}
              description={"Select only BAM or BED file(s)"}
            />
          </Item>
          <Item key={"send-to-analyst"}>
            <MenuOptionItem
              title={"Analyst"}
              id={"send-to-analyst"}
              description={"Select only GDA, ABS or REL file(s)"}
            />
          </Item>
        </Section>
      </MenuTriggerButton>

      <HeaderDivider key={"file-browser-widget-divider2"} />
      <TriggerButton
        key={"file-browser-widget-copy"}
        isDisabled={disabledCopyOperation}
        onPress={handleClickCopyButton}
        icon={"content_copy"}
        label={"Copy"}
        showLabel={false}
      />

      <TriggerButton
        key={"file-browser-widget-cut"}
        isDisabled={disabledCutOperation}
        onPress={handleClickCutButton}
        icon={"content_cut"}
        label={"Cut"}
        showLabel={false}
      />

      <TriggerButton
        key={"file-browser-widget-paste"}
        isDisabled={disabledPasteOperation}
        onPress={handleClickPasteButton}
        icon={fileOperationInProgress ? "content_paste_go" : "content_paste"}
        tooltip={
          filesToBePasted?.length
            ? `Paste the following files: ${filesToBePasted.map((file) => file.humanReadablePath).join(", ")}`
            : undefined
        }
        label={"Paste"}
        showLabel={false}
        badge={filesToBePasted?.length}
      />

      <HeaderDivider key={"file-browser-widget-divider3"} />
      <TriggerButton
        id={"files-page-action-delete"}
        icon={"delete"}
        onPress={() => setShowDeleteDialog(true)}
        intent={TriggerButtonIntent.Danger}
        isDisabled={
          disabledDeletion
            ? props.selectedFiles.length === 0
              ? true
              : "At least one selected resource cannot be deleted"
            : false
        }
        label={"Delete"}
        showLabel={false}
      />
      {showDeleteDialog && (
        <FilesDeletionControlledAlertDialog
          selectedFiles={props.selectedFiles}
          onCancel={() => setShowDeleteDialog(false)}
          onDeleted={() => {
            setShowDeleteDialog(false);
            void props.refreshCurrentFolder();
          }}
        />
      )}

      {projectFilesForGenomeViewer && (
        <BamFileValidationDialog
          key="file-bam-validation-dialog"
          projectFiles={projectFilesForGenomeViewer}
          onFileAssociationsUpdated={void props.refreshCurrentFolder}
          onClose={() => {
            setProjectFilesForGenomeViewer(undefined);
          }}
        />
      )}
    </HStack>
  );
};
